import {BatchRequest} from '../../../../services/proto/core/batchRequest';
import {priority} from '@priority-software/priority-proto-api/lib';
import {getAdditionalProps} from '../../../../services/proto/core/loginProps';
import {CachedFields} from '../../../../stores/Form/FormsCache';
import {batchKeys} from '../../../../services/proto/core/constants';
import {APIActionStatus, Form, formsStore} from '../../../../stores/Form';
import {handleScreenMessage} from '../screen';

type ResponseType = [
    undefined | priority.netitems.form.ISubform,
    undefined | priority.netitems.form.IColumnsRs,
    undefined | priority.netitems.form.IFetchRs
];

const getSubformColumns = (
    form: Form,
    subformName: string,
    hiddenFields = [],
    ignoreCache: boolean = false
) => {
    const session = form.session;
    const batchRequest = new BatchRequest(session);

    batchRequest.addTask({
        key: batchKeys.Subform,
        request: new priority.netitems.form.SubformRq({
            Fname: subformName,
            Keys: form.records[form.currentRow || 0].keys,
            Session: form.session,
            Properties: getAdditionalProps(),
            hiddenFields
        })
    });

    batchRequest.addTask({
        key: batchKeys.Columns,
        request: new priority.netitems.form.ColumnsRq({
            Session: session,
            Properties: getAdditionalProps()
        }),
        cacheData: !ignoreCache ? {key: CachedFields.columns, formName: subformName} : undefined
    });

    return batchRequest;
};

export const startAndGetSubformColumns = async (
    form: Form,
    subformName: string,
    hiddenFields = [],
    ignoreCache = false
) => {
    const batchRequest = getSubformColumns(form, subformName, hiddenFields, ignoreCache);
    const res = await batchRequest.getData();
    return res[1]?.Columns;
};

export const startAndFetchSubform = async (
    form: Form,
    subformName: string,
    fetchMax: number = 0,
    hiddenFields = []
) => {
    const responses = await getStandAloneSubformBatchData(
        form,
        subformName,
        fetchMax,
        hiddenFields
    );
    const [startRs, columnRs, fetchRs] = responses;

    if (startRs) {
        const isFormOk = await handleScreenMessage(startRs);
        if (isFormOk) {
            if (columnRs) {
                const subForm = new Form(subformName, startRs, columnRs, form.level + 1);

                return await handleScreenMessage(fetchRs, {}, () => {
                    subForm.setRecords(fetchRs.Records);
                    subForm.setShouldFetchMore(
                        fetchMax > 0 ? fetchRs.Records?.length > fetchMax : false
                    );
                    subForm.setParentForm(form);
                    formsStore.getScreenBySession(form.session).setActiveLevel(form.level + 1);
                });
            }
        }
    }

    return Promise.resolve(APIActionStatus.blocked);
};

export const getStandAloneSubformBatchData = async (
    form: Form,
    subformName: string,
    fetchMax,
    hiddenFields = []
) => {
    const batchRequest = getSubformColumns(form, subformName, hiddenFields);

    batchRequest.addTask({
        key: batchKeys.Fetch,
        request: new priority.netitems.form.FetchRq({
            Session: form.session,
            Properties: getAdditionalProps(),
            Filters: [],
            Sort: form.sort,
            Top: fetchMax ? fetchMax + 1 : 0,
            Attachments: priority.netitems.form.AttachmentOptions.Attach_NoData
        })
    });

    const res = await batchRequest.getData();
    return [res[0]?.Subform, res[1]?.Columns, res[2]?.Fetch] as ResponseType;
};
